import React, { Suspense, useEffect, useRef, useState } from "react";
import DataLoader from "../../../../components/ui/dataLoader/DataLoader";
import FormCreator from "../../../../components/Forms/FormCreator";
import Accordion from "../../../../components/Accordion/Accordion";
import { AddRequirementData } from "./data/AddRequirement.data";
import Buttons from "../../../../components/ui/button/Buttons";
import CenterModel from "../../../../components/ui/centerModel/CenterModel";
import { AdditionalFieldsData } from "./data/AdditionalFields.data";
import { useNavigate } from "react-router-dom";
import SwalAlert from "../../../../services/swalService/SwalService";
import { useLazyGetAllDropdownForCRUDCategoryQuery, useSubmitRequirmentInformationMutation } from "../../../../app/services/Crud/crudApi";
import { setAllDropDownField } from "../Utils/dropdownUtils";
import ToastService from "../../../../services/toastService/ToastService";
import { getAuthProps } from "../../../../lib/authenticationLibrary";

const AddRequirementForm = () => {
  const addRequirementRefs = useRef();
  const additionalFieldRef = useRef();
  const { success, error, confirm } = SwalAlert()
  const navigate = useNavigate();
  const [ formData ] = useState( AddRequirementData );
  const [ additionalFieldsForm, setAdditionalFieldsForm ] = useState( AdditionalFieldsData );
  const [ rows, setRows ] = useState( [] );
  const [ showAcceptModal, setShowAcceptModal ] = useState( false );
  const [ editId, setEditId ] = useState( null );
  const [ getDropdowns, { data: dropdownData, isLoading: dropdownLoading }, ] = useLazyGetAllDropdownForCRUDCategoryQuery();
  const [ submitRequirmentInformation, { data: submitData, isLoading: submitLoading }, ] = useSubmitRequirmentInformationMutation();
  
  useEffect(() => {
      getDropdowns();
    }, []);
  
  useEffect(() => {
    if (!dropdownLoading && dropdownData) {
      setAllDropDownField(dropdownData);
    }
  }, [ dropdownLoading, dropdownData ] );

  useEffect(() => {
    if (!submitLoading && submitData) {
      if (submitData?.errorMessage === "Requirement Name Already Exists.") {
        ToastService.error("Requirement name already exists.")
      }
      else {
        success("Requirement information submitted successfully")
        navigate( "/crudRequirement" );
      }
    }
  }, [ submitLoading, submitData ] );
  
  const handleAddField = () => {
    setEditId( null );

    const resetForm = {
      ...additionalFieldsForm,
      initialState: {
        label: "",
        inputType: "",
        mandatory: "",
        instructions: "",
      },
    };

    setAdditionalFieldsForm( resetForm );
    setShowAcceptModal( true );
  };

  const handleEdit = ( id ) => {
    const selected = rows.find( ( row ) => row.id === id );
    setEditId( id );
    const updatedForm = { ...additionalFieldsForm,
      initialState: {
        ...additionalFieldsForm.initialState,
        label: selected?.label,
        inputType: selected?.inputType,
        mandatory: selected?.mandatory,
        instructions: selected?.instructions,
      },
    };

    setAdditionalFieldsForm( updatedForm );
    setShowAcceptModal( true );
  };

  const closeAcceptModal = () => {
    setShowAcceptModal( false );

    const resetForm = {
      ...additionalFieldsForm,
      initialState: {
        label: "",
        inputType: "",
        mandatory: "",
        instructions: "",
      },
    };

    setAdditionalFieldsForm( resetForm );
    setEditId( null );
  };

  const handleSubmitField = () => {
    const data = additionalFieldRef.current?.getFormData();

    if ( !data || !data.label ) {
      error( "Please fill in all mandatory fields to continue.", "" )
      return;
    }

    const formattedData = {
      label: data.label,
      inputType: data.inputType,
      mandatory: data.mandatory,
      instructions: data.instructions,
    };

    if ( editId ) {
      setRows( ( prev ) => prev.map( ( row ) => row.id === editId ? { ...formattedData, id: editId } : row ));
      success( "Record updated successfully." )
    } else {
      setRows( ( prev ) => [...prev, { ...formattedData, id: Date.now() },] );
      success( "Record added successfully." )
    }

    closeAcceptModal();
  };

  const handleSubmit = () => {
    const authData = getAuthProps();

    const data = addRequirementRefs?.current?.getFormData();
    if ( !data ) {
      error( "Please fill in all mandatory fields to continue.", "" )
      return;
    }

    const mappedAdditionalFields = rows.map((item) => ({
      Label: item.label || null,
      AdditionalFieldIntructions: item.instructions || null,
      IsTextBox: item.inputType === "Text Box",
      IsRating: item.inputType === "L1-L4 Rating",
      IsTickRating: item.inputType === "Tick Rating",
      IsMandatory: item.mandatory === "Yes",
      CreatedBy : 115,
      CreatedAppId : authData?.adminUserId
    } ) );

    let request = {
      ChannelId: data?.channel?.value || data?.channel || null,
      RequirementSectionId: data?.requirementSection?.value || data?.requirementSection || null,
      RequirementName: data?.requirementName || null,
      RequirementDescription: data?.requirementDescription || null,
      DocumentToBeSubmitted: data?.documentToBeSubmitted || null,
      StatusId: 2,
      AdditionalField: mappedAdditionalFields,
    };
    submitRequirmentInformation( request );
  };

  const handleDelete = ( id ) => {
    confirm( 'Delete', "Are you sure you want to delete", 'Yes', 'Cancel' )
      .then( value => {
        if ( value ) {
          setRows( rows.filter( ( row ) => row.id !== id ) );
          success( "Record deleted successfully." )
        }
      } )
  };

  const handleBack = () => {
    navigate( "/crudRequirement" );
  };

  return (
    <div className="add-requirement">
      <Suspense fallback={ <DataLoader /> }>
        <Accordion
          title="Requirement Information"
          className="mb-4"
          button={ false }
          accordion={ false }
        >
          <div className="row">
            { dropdownData &&
              <FormCreator ref={ addRequirementRefs } { ...formData } />
            }
          </div>

          <div className="additional-field-sec mt-5">
            <label>Additional Fields</label>
            <Buttons
              buttonText="Add Field"
              buttonTypeClassName="add-btn"
              onClick={ handleAddField }
            />
          </div>

          <div className="additional-field-table">
            <div className="label-table">
              <table>
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Input Type</th>
                    <th>Mandatory</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  { rows.length > 0 ? (
                    rows.map( ( row ) => (
                      <tr key={ row.id }>
                        <td>{ row.label }</td>
                        <td>{ row.inputType }</td>
                        <td>{ row.mandatory }</td>
                        <td className="actions">
                          <span
                            className="edit"
                            onClick={ () => handleEdit( row.id ) }
                          >
                            Edit
                          </span>
                          <span
                            className="delete"
                            onClick={ () => handleDelete( row.id ) }
                          >
                            Delete
                          </span>
                        </td>
                      </tr>
                    ) )
                  ) : (
                    <tr>
                      <td colSpan="4" style={ { textAlign: "center" } }>
                        No records found
                      </td>
                    </tr>
                  ) }
                </tbody>
              </table>
            </div>
          </div>
        </Accordion>

        <div className="add-requirement-btn-wrapper mt-5">
          <Buttons
            buttonText="Back"
            buttonTypeClassName="btn btn-secondary mb-2"
            onClick={ handleBack }
          />

          <div className="save-btn-wrapper">
            <Buttons
              buttonText="Approve"
              buttonTypeClassName="btn btn-secondary mb-2 disabled"
              disabled={ true }
            />
            <Buttons
              buttonText="Save"
              buttonTypeClassName="add-btn"
              onClick={ handleSubmit }
              disabled={ submitLoading }
              isLoading={ submitLoading }
            />
          </div>
        </div>

        { showAcceptModal && (
          <CenterModel
            show={ showAcceptModal }
            modelTitle={ "Additional Fields" }
            handleClose={ closeAcceptModal }
            modelWidth={ "additional-field-modal" }
          >
            <FormCreator ref={ additionalFieldRef } { ...additionalFieldsForm } />

            <div className="btn-center-wrapper">
              <Buttons
                buttonText="Submit"
                buttonTypeClassName="add-btn"
                onClick={ handleSubmitField }
              />
            </div>
          </CenterModel>
        ) }
      </Suspense>
    </div>
  );
};

export default AddRequirementForm;